import {
	CONNECTED,
	CONNECTING,
	DISCONNECTED,
	DISCONNECTING,
	INITIALIZING,
	INITIALIZATION_FAILED,
	RECONNECTING,
	desktopNotification,
	playAudio,
	toggleState
} from "./comms";

import { Client as ConversationsClient } from "@twilio/conversations";
import axios from "axios";
import { isEmpty } from "lodash";
import router from "../router";
import store from "../store";
import i18n from "../i18n";
import { bus } from "../main";
import { MessageAuthor, PHONE_CALL_STATUS } from "@/common/const";
import { fullName, compareConversations } from "@/common/utils";
import { conversationsMap, ensureConversationLoaded } from "./conversation-v2";

// ---- Module-level state ----

let conversationsClient = null;

let attemptsCount = 0;

let lastConnectionErrorTime = 0;

let lastTokenUpdateTime = 0;

let loadingConversations = false;

let refreshingToken = false;
const MAX_ATTEMPTS = 5;

// ---- Version detection ----

let _activeComposable = null;

function getVersion() {
	return store?.getters?.["conversation/version"] ?? 1;
}

/**
 * Called by the store to register the active composable instance.
 * Lifecycle functions (reloadConversations, subscribeToInactiveConversations, etc.)
 * delegate to this composable.
 */
export function setActiveComposable(composable) {
	_activeComposable = composable;
}

// ---- Version-aware public API ----

export function getConversation(sid) {
	if (getVersion() >= 2) {
		return conversationsMap.get(sid);
	}
	return conversationsClient?.conversations?.conversations.get(sid);
}

/**
 * Version-aware lookup that, for V2, fetches the conversation from the backend
 * when it is not part of the currently loaded page (e.g. restoring the last
 * opened conversation). For V1 it resolves synchronously from the Twilio client.
 */
export async function ensureConversation(sid) {
	if (getVersion() >= 2) {
		return await ensureConversationLoaded(sid);
	}
	return getConversation(sid);
}

export function getConversationsMap() {
	if (getVersion() >= 2) {
		return conversationsMap;
	}
	return conversationsClient?.conversations?.conversations;
}

export async function sendMessageWithRetry(conversation, content, attributes) {
	if (getVersion() >= 2) {
		// File/media send: the caller builds a FormData with the file under "file".
		// Upload it to the backend, which stores it and sends it as MMS media.
		if (typeof FormData !== "undefined" && content instanceof FormData) {
			content.append("to", conversation.friendlyName);
			return await axios.post("conversations/v2/message/send-media", content);
		}

		return await axios.post("conversations/v2/message/send", {
			to: conversation.friendlyName,
			message: content,
			attributes
		});
	}

	const profile = store.getters["login/profile"];
	const v1Attributes = {
		...(attributes ?? {}),
		userId: profile?.id,
		author: fullName(profile)
	};

	try {
		return await conversation.sendMessage(content, v1Attributes);
	} catch (err) {
		if (err.code === 50400) {
			console.debug("[CONVERSATION-V1] Error 50400 detected, rejoining conversation...");
			try {
				await axios.post("conversations", { to: conversation.friendlyName });
				return await conversation.sendMessage(content, v1Attributes);
			} catch (rejoinError) {
				console.error("[CONVERSATION-V1] Failed to rejoin and resend:", rejoinError);
				throw rejoinError;
			}
		}
		throw err;
	}
}

export async function createConversation(messageObj) {
	if (getVersion() >= 2) {
		return await axios.post("conversations/v2/message/send", messageObj);
	}
	// Use init-only endpoint when no message body is provided (e.g. finding/creating a conversation for a call)
	const hasMessage = messageObj?.message || messageObj?.body;

	return await axios.post(hasMessage ? "conversations" : "conversations?init=true", messageObj);
}

export async function updateConversation(sid, options) {
	if (getVersion() >= 2) {
		const conversation = conversationsMap.get(sid);
		const response = await axios.put(`conversations/v2/sid/${sid}`, options);

		if (conversation && conversationsMap.get(sid) === conversation) {
			conversation.update(response.data);
		}

		return response;
	}
	return await axios.put(`conversations/sid/${sid}`, options);
}

export async function deleteConversation(sid) {
	if (getVersion() >= 2) {
		return await axios.delete(`conversations/devops/sid/${sid}`);
	}
	return await axios.delete(`conversations/sid/${sid}`);
}

export async function deleteConversationMessage(conversationSid, messageSid) {
	if (getVersion() >= 2) {
		return await axios.delete(`conversations/devops/sid/${conversationSid}/messages/${messageSid}`);
	}

	await axios.delete(`/devops/conversations/${conversationSid}/messages/${messageSid}`);
	window.location.reload(); // Reload only for V1
}

export async function cleanupConversations() {
	if (_activeComposable) {
		await _activeComposable.cleanup();
		_activeComposable = null;
	}
}

export async function reloadConversations() {
	if (_activeComposable) {
		return _activeComposable.bootstrap();
	}
}

export function loadConversations(conversationState, limit) {
	if (getVersion() >= 2 && _activeComposable) {
		_activeComposable.subscribeToConversations?.(conversationState, limit);
	}
	// V1 loads all conversations (active + inactive) together — no-op
}

export function unloadConversations() {
	if (getVersion() >= 2) {
		_activeComposable?.unsubscribeFromConversations?.();
	}
}

/** V2 cursor pagination: jump to the first page. */
export function loadFirstConversationsPage(conversationState, limit) {
	if (getVersion() >= 2 && _activeComposable?.loadFirstPage) {
		return _activeComposable.loadFirstPage(conversationState, limit);
	}

	return Promise.resolve();
}

/** V2 cursor pagination: advance to the next page. */
export function loadNextConversationsPage(conversationState, limit) {
	if (getVersion() >= 2 && _activeComposable?.loadNextPage) {
		return _activeComposable.loadNextPage(conversationState, limit);
	}

	return Promise.resolve();
}

/** V2 cursor pagination: return to the previous page. */
export function loadPrevConversationsPage(conversationState, limit) {
	if (getVersion() >= 2 && _activeComposable?.loadPrevPage) {
		return _activeComposable.loadPrevPage(conversationState, limit);
	}

	return Promise.resolve();
}

/**
 * V2 cursor pagination: jump to the last page. Resolves to the 0-based page
 * index, or -1 when it could not be shown.
 */
export function loadLastConversationsPage(conversationState, limit) {
	if (getVersion() >= 2 && _activeComposable?.showLastPage) {
		return _activeComposable.showLastPage(conversationState, limit);
	}

	return Promise.resolve(-1);
}

/**
 * V2 cursor pagination: locate and display the page containing a conversation
 * (e.g. one selected from search). Resolves to the 0-based page index, or -1
 * when the conversation could not be located.
 */
export function loadConversationsPageContaining(conversationState, sid, limit) {
	if (getVersion() >= 2 && _activeComposable?.showPageContaining) {
		return _activeComposable.showPageContaining(conversationState, sid, limit);
	}

	return Promise.resolve(-1);
}

// Re-export V2 data classes / helpers for consumers
export { ConversationMessage, upsertConversation } from "./conversation-v2";

// ---- V1 Twilio composable ----

const useConversation = () => {

	const userGroupIdLoaded = () =>  {
		const loadedUserGroup = store.getters["conversation/userGroupId"];

		return loadedUserGroup && loadedUserGroup === store.getters["login/userGroupId"];
	};

	const onConversationUpdated = async (updatePayload) => {
		console.debug("[CONVERSATION-V1] onConversationUpdated ", updatePayload?.conversation?.sid);
		const { updateReasons, conversation } = updatePayload;

		await store.dispatch("conversation/updateConversation", { updateReasons, conversation });

		if (updateReasons.includes("attributes")) {
			if (conversation.attributes.hasNotification &&
				!conversation.attributes.hasIncompleteFollowup) {
				playAudio();
				desktopNotification();
			}
		}
	};

	const onConversationLeft = (conversation) => {
		console.debug("[CONVERSATION-V1] onConversationLeft ", conversation.sid);
		store.dispatch("conversation/removeConversation", conversation);
	};

	const onConversationJoined = async (conversation) => {
		console.debug("[CONVERSATION-V1] onConversationJoined ", conversation.sid, " attributes ", conversation.attributes);

		await store.dispatch("conversation/addConversation", conversation);

		if (conversation.lastMessage?.index >= 0) {
			playAudio();
			desktopNotification();
		}
	};

	const onMessageAdded = (message) => {
		console.debug("[CONVERSATION-V1] onMessageAdded");
		if (message.attributes.isFlowMessage) {
			return;
		}

		const convo = store.getters["conversation/singleConversation"];

		if (
			router.currentRoute.name == "conversations" &&
			message.conversation.sid === convo?.sid
		) {
			store.commit("conversation/addSingleMessage", message);
			store.commit("conversation/setNewMessage", true);
		}

		const isCallCompleted = message.state?.attributes?.status === PHONE_CALL_STATUS.COMPLETED;

		const type = message.state?.attributes?.type;
		const isPhoneCallMessage = type === "call";
		const isEmailMessage = type === "email";
		const isTextMessage = type === "text" && isEmpty(message.state.attributes);
		const inbound = message.state?.direction === "inbound" ||
			message?.author === MessageAuthor.Contact ||
			// V1: patients author with their phone number, i.e. the friendlyName
			// of the message's own conversation (not the selected one)
			message.state?.author === message.conversation?.friendlyName;

		// Notify for every conversation, not just the selected one: the
		// hasNotification round-trip via onConversationUpdated is silent when the
		// conversation is already unread (attributes unchanged → no event reason).
		if (
			inbound &&
			(isEmailMessage || (isPhoneCallMessage && !isCallCompleted) || isTextMessage)
		) {
			playAudio();
			desktopNotification();
		}
	};

	const onMessageUpdated = (updatePayload) => {
		const { message, updateReasons } = updatePayload;

		console.debug("[CONVERSATION-V1] onMessageUpdated", message?.sid, "reasons:", updateReasons);

		// Only handle attribute updates
		if (!updateReasons?.includes("attributes")) {
			return;
		}

		// Check if this message belongs to the current conversation
		if (message.conversation.sid !== store.getters["conversation/singleConversation"]?.sid) {
			return;
		}

		// Parse attributes to check for transcription updates
		const attributes = message.state?.attributes || {};
		const transcriptions = attributes.transcriptions;

		if (transcriptions) {
			console.debug("[CONVERSATION-V1] Transcription update detected for message:", message.sid);
			// Emit event for components to listen to
			bus.$emit("transcription-status-changed", {
				messageSid: message.sid,
				conversationSid: message.conversation.sid,
				transcriptions
			});
		}

		// Update the message in the store
		store.commit("conversation/updateSingleMessage", message);
	};

	// Client connection and token management
	// --------------------------------------

	const authenticate = async () => {
		if (getVersion() >= 2) return;

		console.debug(`[CONVERSATION-V1] Authenticating client... (${attemptsCount+1}/${MAX_ATTEMPTS})`);

		try {
			lastTokenUpdateTime = 0;

			const { data: { jwt } } = await axios.get("conversations/tokens");

			if (getVersion() >= 2) return;

			if (!jwt) {
				throw new Error("No JWT received from server");
			}

			if (conversationsClient) {
				try {
					await conversationsClient.updateToken(jwt);
					lastTokenUpdateTime = Date.now();
				} catch (error) {
					console.error("[CONVERSATION-V1] Error updating token:", error);
					// If token update fails, we need to recreate the client
					await releaseClient();
					throw new Error("Token update failed, client needs to be recreated");
				}
			} else {
				const client = await ConversationsClient.create(jwt);

				if (getVersion() >= 2) {
					await client.shutdown();
					return;
				}

				conversationsClient = client;
				loadConnectionEvents();
			}

			if (getVersion() >= 2) return;

			store.commit("conversation/setUserGroupId", store.getters["login/userGroupId"]);
		} catch (err) {
			if (getVersion() >= 2) return;

			console.error("[CONVERSATION-V1] Error authenticating token:", err);

			// If we never started once, return an error with a retry button
			if (!userGroupIdLoaded()) {
				toggleState(INITIALIZATION_FAILED);
				return;
			}

			// Retry indefinitely
			setTimeout(authenticate, 3000);
		}
	};

	const refreshToken = async () => {
		if (getVersion() >= 2) return;

		if (refreshingToken) {
			console.debug("[CONVERSATION-V1] Already refreshing. Skipped");
			return;
		}

		if (!conversationsClient) {
			authenticate();
			return;
		}

		// Add a delay to avoid multiple rapid token updates
		if (Date.now() - lastTokenUpdateTime < 5000) {
			return;
		}

		console.debug(`[CONVERSATION-V1] refreshing token (${attemptsCount+1}/${MAX_ATTEMPTS})`);

		try {
			refreshingToken = true;
			const { data: { jwt } } = await axios.get("conversations/refreshTokens");

			if (getVersion() >= 2 || !conversationsClient) return;

			await conversationsClient.updateToken(jwt);
			lastTokenUpdateTime = Date.now();

			attemptsCount = 0;
			console.debug("[CONVERSATION-V1] Token updated successfully");
		} catch (err) {
			if (getVersion() >= 2) return;

			console.error("[CONVERSATION-V1] Error refreshing token:", err);

			if (attemptsCount < MAX_ATTEMPTS) {
				attemptsCount++;
				const delay = 1000 * attemptsCount;

				setTimeout(refreshToken, delay);
			} else {
				attemptsCount = 0;
				setTimeout(authenticate, 2000);
			}
		} finally {
			refreshingToken = false;
		}
	};

	const loadExistingConversations = async () => {
		if (getVersion() >= 2) return;

		if (loadingConversations) {
			console.debug("[CONVERSATION-V1] loadExistingConversations is already running");
			return;
		}
		console.debug(`[CONVERSATION-V1] Loading existing conversations ... (${attemptsCount+1}/${MAX_ATTEMPTS})`);

		loadingConversations = true;

		try {
			removeConversationEvents();

			store.commit("conversation/setLoadingAllConversations", true);

			let actives = [];

			let inactives = [];

			let addedConversations = {};

			let notifications = new Set();

			let notificationInactive = new Set();

			const start = new Date().getTime();

			let pager = await conversationsClient.getSubscribedConversations({ pageSize: 100 });

			// Collect conversation objects and sort once after pagination: a sorted
			// insert per item costs O(n²) splices plus a sid→conversation lookup per
			// binary-search probe, while the objects are already in hand here.
			const processConversations = (items) => {
				items.forEach(c => {
					addedConversations[c.sid] = true;

					if (c.state.current === "active") {
						if (c.channelState.attributes.hasNotification) {
							notifications.add(c.sid);
						}
						actives.push(c);
					} else {
						if (c.channelState.attributes.hasNotification) {
							notificationInactive.add(c.sid);
						}
						inactives.push(c);
					}
				});
			};

			processConversations(pager.items);

			while (pager.hasNextPage && getVersion() < 2) {
				pager = await pager.nextPage();
				processConversations(pager.items);
			}

			if (getVersion() >= 2) return;

			const activeSids = actives
				.sort((a, b) => compareConversations(a, b))
				.map((c) => c.sid);

			const inactiveSids = inactives
				.sort((a, b) => compareConversations(a, b))
				.map((c) => c.sid);

			// Set all conversations once pagination is complete
			store.commit("conversation/setAll", {
				actives: activeSids,
				inactives: inactiveSids,
				notifications,
				notificationInactive,
				addedConversations
			});

			console.debug(`[CONVERSATION-V1] loadExistingConversations took ${new Date().getTime() - start} ms`);

			attemptsCount = 0;

			loadConversationEvents();

			toggleState(CONNECTED);
		} catch (err) {
			if (getVersion() >= 2) return;

			console.error("[CONVERSATION-V1] Error loading existing conversations:", err);
			if (conversationsClient && attemptsCount < MAX_ATTEMPTS) {
				attemptsCount++;
				const delay = 1000 * attemptsCount;

				setTimeout(loadExistingConversations, delay);
			} else {
				attemptsCount = 0;
				setTimeout(refreshToken, 3000);
			}
		} finally {
			loadingConversations = false;
			store.commit("conversation/setLoadingAllConversations", false);
		}
	};

	const onConnectionError = async (error) => {
		console.error("[CONVERSATION-V1] onConnectionError:", error);
		const now = Date.now();

		if (error?.errorCode === 20104) {
			refreshToken();
			return;
		}

		if (now - lastConnectionErrorTime > 10000) {
			lastConnectionErrorTime = now;

			// Only show connection unstable alert on desktop
			if (window.innerWidth >= 600) {
				store.commit("alerts/add", {
					type: "error",
					message: i18n.t("error.connection-unstable"),
					timeout: true
				});
			}
		}
	};

	const onConnectionStateChanged = async (state) => {
		switch (state) {
			case CONNECTING:
				console.debug("[CONVERSATION-V1] Client connecting...");
				if (!userGroupIdLoaded()) {
					toggleState(CONNECTING);
				}
				break;

			case CONNECTED:
				console.debug("[CONVERSATION-V1] Client connected");
				lastConnectionErrorTime = 0;

				if (!store.getters["conversation/loaded"]) {
					loadExistingConversations();
				} else {
					toggleState(CONNECTED);
				}
				break;

			case DISCONNECTING:
				console.debug("[CONVERSATION-V1] Client disconnecting...");
				break;

			case RECONNECTING:
				console.debug("[CONVERSATION-V1] Client reconnecting...");
				break;

			case DISCONNECTED:
				console.debug("[CONVERSATION-V1] Client disconnected");
				if (!refreshingToken) {
					setTimeout(refreshToken, 2000);
				}
				break;
		}
	};

	const removeConversationEvents = () => {
		console.debug("[CONVERSATION-V1] removeConversationEvents");

		if (!conversationsClient) {
			return;
		}

		try {
			conversationsClient.off("conversationJoined", onConversationJoined);
			conversationsClient.off("conversationUpdated", onConversationUpdated);
			conversationsClient.off("conversationLeft", onConversationLeft);
			conversationsClient.off("messageAdded", onMessageAdded);
			conversationsClient.off("messageUpdated", onMessageUpdated);

		} catch (err) {
			console.error("[CONVERSATION-V1] Error while removing conversation events ", err);
			throw err;
		}
	};

	const loadConversationEvents = () => {
		console.debug("[CONVERSATION-V1] loadConversationEvents");
		if (!conversationsClient) {
			return;
		}

		try {
			conversationsClient.off("conversationJoined", onConversationJoined);
			conversationsClient.off("conversationUpdated", onConversationUpdated);
			conversationsClient.off("conversationLeft", onConversationLeft);
			conversationsClient.off("messageAdded", onMessageAdded);
			conversationsClient.off("messageUpdated", onMessageUpdated);

			conversationsClient.on("conversationJoined", onConversationJoined);
			conversationsClient.on("conversationUpdated", onConversationUpdated);
			conversationsClient.on("conversationLeft", onConversationLeft);
			conversationsClient.on("messageAdded", onMessageAdded);
			conversationsClient.on("messageUpdated", onMessageUpdated);
		} catch (err) {
			console.error("[CONVERSATION-V1] Error while adding conversation events ", err);
			throw err;
		}
	};

	const removeConnectionEvents = () => {
		console.debug("[CONVERSATION-V1] removeConnectionEvents");

		if (!conversationsClient) {
			return;
		}

		try {
			conversationsClient.off("tokenAboutToExpire", refreshToken);
			conversationsClient.off("connectionStateChanged", onConnectionStateChanged);
			conversationsClient.off("connectionError", onConnectionError);
		} catch (err) {
			console.error("Error while removing connection events ", err);
			throw err;
		}
	};

	const loadConnectionEvents = () => {
		console.debug("[CONVERSATION-V1] loadConnectionEvents");
		if (!conversationsClient) {
			return;
		}

		try {
			conversationsClient.off("tokenAboutToExpire", refreshToken);
			conversationsClient.off("connectionStateChanged", onConnectionStateChanged);
			conversationsClient.off("connectionError", onConnectionError);

			conversationsClient.on("tokenAboutToExpire", refreshToken);
			conversationsClient.on("connectionStateChanged", onConnectionStateChanged);
			conversationsClient.on("connectionError", onConnectionError);
		} catch (err) {
			console.error("[CONVERSATION-V1] Error while adding connection events ", err);
			throw err;
		}
	};

	const bootstrap = async () => {
		if (getVersion() >= 2) return;

		console.debug("[CONVERSATION-V1] bootstrap");
		toggleState(INITIALIZING);
		await authenticate();
	};

	const cleanup = async (commit) => {
		if (!conversationsClient) return;
		
		console.debug("[CONVERSATION-V1] cleanup");
		attemptsCount = 0;
		if (commit) {
			commit("release");
		} else {
			store.commit("conversation/release");
		}
		await releaseClient();
	};

	const releaseClient = async () =>{
		console.debug("[CONVERSATION-V1] releaseClient");
		if (!conversationsClient) return;

		removeConversationEvents();
		removeConnectionEvents();

		try {
			await conversationsClient.shutdown();
		} catch (err) {
			console.warn("[CONVERSATION-V1] Error during client shutdown:", err);
		}

		conversationsClient = null;
	};

	return {
		bootstrap,
		toggleState,
		cleanup
	};
};

export { useConversation };
export default useConversation;
